import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { LegalPersonTags } from '@dealstack/shared/lib/generated';
import { Button, Flex, Menu, Text, FloatingPosition } from '@mantine/core';
import React, { memo, useEffect } from 'react';
import { makeTag } from '@dealstack/frontend/app/pages/Project/Cap/common/EntityTags/tags.helper.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	tags: LegalPersonTags[];
	visibleTagCount?: number;
	dropdownPosition?: FloatingPosition;
	blockDropdown?: boolean;
	// Optional: when set, each rendered tag chip gets a per-tag suffixed testId
	// (`${testIdBase}_${tagEnumValue}`). Used by the entity-overview read-mode tag
	// list. Omit in places where tags appear inside a repeated context (e.g. the
	// sidebar PersonCard) so we don't produce duplicate testIds across rows.
	testIdBase?: TEST_ELEMENT_IDS;
}

const EntityTags = ({
	tags,
	visibleTagCount,
	blockDropdown = false,
	dropdownPosition = 'bottom-end',
	testIdBase,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const [visibleItems, setVisibleItems] = React.useState<LegalPersonTags[]>([]);
	const [dropdownItems, setDropdownItems] = React.useState<LegalPersonTags[]>([]);
	const [opened, setOpen] = React.useState(false);

	useEffect(() => {
		if (visibleTagCount === 0) {
			setVisibleItems([]);
			setDropdownItems(tags);
			return;
		} else if (!visibleTagCount) {
			setVisibleItems(tags);
			setDropdownItems([]);
			return;
		}

		setVisibleItems(tags.slice(0, visibleTagCount));
		setDropdownItems(tags.slice(visibleTagCount));
	}, [tags, visibleTagCount]);

	return tags?.length ? (
		<Flex gap="0.25rem" wrap="wrap">
			{visibleItems.map((tag) => makeTag(tag, tCapManage, testIdBase))}

			{dropdownItems?.length ? (
				<Menu
					shadow="md"
					position={dropdownPosition}
					onClose={() => setOpen(false)}
					opened={opened}
					onOpen={() => !blockDropdown && setOpen(true)}
				>
					<Menu.Target>
						<Button
							variant="default"
							disabled={blockDropdown}
							bg="gray.4"
							p="0 0.5rem"
							radius="xl"
							sx={(theme) => ({
								'&:hover': {
									backgroundColor: theme.colors.gray[4],
								},
								'&:disabled': {
									backgroundColor: theme.colors.gray[4],
									opacity: 1,
								},
							})}
						>
							<Text fz="sm" fw={400} c="dark.4">
								+{dropdownItems.length}
							</Text>
						</Button>
					</Menu.Target>

					<Menu.Dropdown
						sx={() => ({
							borderRadius: '0.5rem',
							boxShadow: `0px 16px 32px 0px #91969833`,
						})}
						pb="0"
					>
						{dropdownItems?.map((tag) => (
							<Menu.Item pb="0.25rem" key={tag} disabled={true}>
								{makeTag(tag, tCapManage, testIdBase)}
							</Menu.Item>
						))}
					</Menu.Dropdown>
				</Menu>
			) : null}
		</Flex>
	) : null;
};

const Memoized = memo(EntityTags);

export { Memoized as EntityTags };
